import React, { useState, useEffect } from "react";
import {
  Card,
  FormLayout,
  Select,
  InlineError,
  Checkbox,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import moment from "moment";

export default function OpeningHours(props) {
  const { t } = useTranslation();
  const timeSheetAttributes = props.location.time_sheet_attributes;
  const [useOpeningHours, setUseOpeningHours] = useState(!!timeSheetAttributes);

  const formatTime = (time) => moment(time).format("HH:mm");
  const formatDayNum = (dayNum) => moment().day(dayNum).format("dddd");

  const openingOptions = [
    { label: t("location.openingHours.statusOpen"), value: "open" },
    { label: t("location.openingHours.statusOpen24h"), value: "24h" },
    { label: t("location.openingHours.statusClosed"), value: "closed" },
  ];

  function allTimes() {
    var timeStops = [];
    var startTime = moment().startOf("day");
    var endTime = moment().endOf("day");

    while (startTime <= endTime) {
      timeStops.push(new moment(startTime));
      startTime.add("m", 15);
    }
    return timeStops;
  }

  const timeOptions = allTimes().map((time) => {
    return { label: time.format("h:mmA"), value: time.format("HH:mm") };
  });

  function updateTimeEntry(day_key, key, value) {
    let newTimeSheetAttributes = { ...props.location.time_sheet_attributes };
    newTimeSheetAttributes[`${day_key}_${key}`] = value;
    props.setLocation({
      ...props.location,
      time_sheet_attributes: newTimeSheetAttributes,
    });
  }

  function updateStatus(day_key, status) {
    let newLocation = { ...props.location };
    if (status == "24h") {
      newLocation.time_sheet_attributes[`${day_key}_opening_time`] = "00:00";
      newLocation.time_sheet_attributes[`${day_key}_closing_time`] = "24:00";
    }
    if (status == "closed") {
      newLocation.time_sheet_attributes[`${day_key}_opening_time`] = "00:00";
      newLocation.time_sheet_attributes[`${day_key}_closing_time`] = "00:00";
    }
    if (status == "open") {
      newLocation.time_sheet_attributes[`${day_key}_opening_time`] = "09:00";
      newLocation.time_sheet_attributes[`${day_key}_closing_time`] = "17:00";
    }
    props.setLocation(newLocation);
  }

  function updateUseOpeningHours(willUseOpeningHours) {
    if (willUseOpeningHours && timeSheetAttributes) {
      useExistingTimesheetAttributesAndRemoveDelete();
    }
    if (!willUseOpeningHours && timeSheetAttributes) {
      deleteExistingTimesheetAttributes();
    }
    if (willUseOpeningHours && !timeSheetAttributes) {
      createDefaultTimesheetAttributes();
    }
    if (!willUseOpeningHours && !timeSheetAttributes) {
      deleteTimesheetAttributes();
    }
    setUseOpeningHours(willUseOpeningHours);
  }

  function useExistingTimesheetAttributesAndRemoveDelete() {
    let newLocation = { ...props.location };
    if (newLocation.time_sheet_attributes._destroy) {
      newLocation.time_sheet_attributes._destroy = undefined;
    }
    props.setLocation(newLocation);
  }

  function createDefaultTimesheetAttributes() {
    let newLocation = { ...props.location };
    const defaultTimesheetAttributes = {
      monday_closing_time: "17:00",
      monday_opening_time: "09:00",
      tuesday_closing_time: "17:00",
      tuesday_opening_time: "09:00",
      wednesday_closing_time: "17:00",
      wednesday_opening_time: "09:00",
      thursday_closing_time: "17:00",
      thursday_opening_time: "09:00",
      friday_closing_time: "17:00",
      friday_opening_time: "09:00",
      saturday_closing_time: "00:00",
      saturday_opening_time: "00:00",
      sunday_closing_time: "00:00",
      sunday_opening_time: "00:00",
    };
    newLocation.time_sheet_attributes = defaultTimesheetAttributes;
    props.setLocation(newLocation);
  }

  function deleteExistingTimesheetAttributes() {
    if (timeSheetAttributes.id) {
      let newLocation = { ...props.location };
      newLocation.time_sheet_attributes._destroy = true;
      props.setLocation(newLocation);
    } else {
      deleteTimesheetAttributes();
    }
  }

  function deleteTimesheetAttributes() {
    let newLocation = { ...props.location };
    newLocation.time_sheet_attributes = null;
    props.setLocation(newLocation);
  }

  const errorMarkup = props.errors && props.errors["opening_hours.base"] && (
    <InlineError message={props.errors["opening_hours.base"].join(" ")} />
  );

  const dayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return <>
    <Card.Section title={t("location.openingHours.title")}>
      <Checkbox
        label={t("location.openingHours.checkboxLabel")}
        checked={useOpeningHours}
        onChange={(value) => updateUseOpeningHours(value)}
      />
    </Card.Section>
    {useOpeningHours && timeSheetAttributes ? (
      <Card.Section>
        <FormLayout>
          {dayOrder.map((day_key) => {
            const openingTime =
              timeSheetAttributes[`${day_key}_opening_time`];
            const closingTime =
              timeSheetAttributes[`${day_key}_closing_time`];

            const prettyDay = t(`location.openingHours.days.${day_key}`);

            let status;
            status = "open";
            if (openingTime == "00:00" && closingTime == "00:00")
              status = "closed";
            if (openingTime == "00:00" && closingTime == "24:00")
              status = "24h";

            return (
              <FormLayout.Group condensed title={prettyDay} key={day_key}>
                <Select
                  label={t("location.openingHours.statusOpen")}
                  options={openingOptions}
                  value={status}
                  onChange={(status) => updateStatus(day_key, status)}
                  labelHidden
                />
                {status == "open" && (
                  <Select
                    label={t("location.openingHours.openingTimeLabel")}
                    options={timeOptions}
                    value={openingTime}
                    onChange={(value) =>
                      updateTimeEntry(day_key, "opening_time", value)
                    }
                    labelHidden
                  />
                )}
                {status == "open" && (
                  <Select
                    label={t("location.openingHours.closingTimeLabel")}
                    options={timeOptions}
                    value={closingTime}
                    onChange={(value) =>
                      updateTimeEntry(day_key, "closing_time", value)
                    }
                    labelHidden
                  />
                )}
              </FormLayout.Group>
            );
          })}
          {errorMarkup}
        </FormLayout>
      </Card.Section>
    ) : errorMarkup ? (
      errorMarkup
    ) : null}
  </>;
}
